/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {useTypedSelector} from "helpers/reducers";
import Loader from "components/Loader";
import clsx from "clsx";
import LinkTo from "helpers/LinkTo";
import {useTranslation} from "react-i18next";
import {AtSymbolIcon, LinkIcon, PhoneIcon} from "@heroicons/react/20/solid";
import {MapIcon} from "@heroicons/react/24/outline";
import {HallOpeningHour} from "types/entities/HallOpeningHour";
import FacebookFIcon from "components/Icons/FontAwesome/FacebookFIcon";
import InstagramIcon from "components/Icons/FontAwesome/InstagramIcon";
import TikTokIcon from "components/Icons/FontAwesome/TikTokIcon";
import YoutubeIcon from "components/Icons/FontAwesome/YoutubeIcon";
import LinkedInInIcon from "components/Icons/FontAwesome/LinkedInInIcon";

interface GroupedData {
    [key: string]: HallOpeningHour[];
}

interface BookingFooterNewProps {
    className?: string;
}

const BookingFooterNew = (props: BookingFooterNewProps) => {
    const hall = useTypedSelector(state => state.hallState.hall);
    const {t} = useTranslation();
    const [openingHours, setOpeningHours] = useState<GroupedData>();

    const getDayName = (dayNumber: number): string => {
        const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
        return daysOfWeek[dayNumber - 1];
    };

    const groupByOpeningHours = (data: HallOpeningHour[]): GroupedData => {
        return data.reduce((result: GroupedData, item) => {
            const key = `${item.opening_hours_from}-${item.opening_hours_to}`;
            if (!result[key]) {
                result[key] = [];
            }
            result[key].push(item);
            return result;
        }, {});
    };

    useEffect(() => {
        setOpeningHours(groupByOpeningHours(hall?.hallOpeningHours ? hall.hallOpeningHours : []));
    }, [hall]);

    if (!hall) {
        return <Loader />;
    }

    return (
        <div className={clsx("BookingFooterNew bg-black text-white inset-x-0 text-sm duration-0 lg:text-base py-6 px-4 xl:px-0", props.className)}>
            <div className={"max-w-full xl:max-w-7xl w-full h-full flex flex-col items-center justify-center m-auto"}>
                <div className={"grid grid-cols-12 gap-x-4 gap-y-8 lg:gap-y-0 leading-4 w-full"}>
                    <div className={"col-span-12 sm:col-span-6 lg:col-span-3 flex flex-col"}>
                        <h2 className={"font-bold mb-4 text-xl text-center sm:text-left"}>
                            {t("Address")} / {t("Contact")}
                        </h2>
                        <div className={"flex flex-col gap-y-1.5 mb-6 text-center sm:text-left"}>
                            <span className={"font-semibold"}>{hall.name}</span>
                            <span>Textilstrasse 7</span>
                            <span>9320 Arbon</span>
                            <span>{t("Switzerland")}</span>
                        </div>
                        <div className={"flex flex-col gap-y-1.5 mb-6"}>
                            <span className={"inline-flex items-center justify-center sm:justify-start"}>
                                <MapIcon className={"w-3 h-3 mr-1 lg:mr-2"} />
                                <a className={"hover:text-arbo-orange"} href={"https://maps.app.goo.gl/YNYb3iz9BxUB7mS36"} rel={"noreferrer"} target={"_blank"}>
                                    {t("Open in Google Maps")}
                                </a>
                            </span>
                        </div>
                        <div className={"flex flex-col gap-y-1.5"}>
                            <span className={"inline-flex items-center justify-center sm:justify-start"}>
                                <PhoneIcon className={"w-3 h-3 mr-1 lg:mr-2"} />
                                <a className={"hover:text-arbo-orange"} href={`tel:${hall.contact}`}>
                                    {hall.contact}
                                </a>
                            </span>
                            <span className={"inline-flex items-center justify-center sm:justify-start"}>
                                <AtSymbolIcon className={"w-3 h-3 mr-1 lg:mr-2"} />
                                <a className={"hover:text-arbo-orange"} href={`mailto:${hall.email}`}>
                                    {hall.email}
                                </a>
                            </span>
                        </div>
                    </div>

                    <div className={"col-span-12 sm:col-span-6 lg:col-span-3 flex flex-col"}>
                        <h2 className={"font-bold mb-4 text-xl text-center sm:text-left"}>{t("Opening hours")}</h2>
                        <table className={"w-full max-w-xs mx-auto sm:mx-0"}>
                            <tbody>
                                {openingHours &&
                                    Object.entries(openingHours).map(([key, hours]) => {
                                        const days = hours.map(hour => t(getDayName(hour.day))).join(", ");
                                        const [openingHoursFrom, openingHoursTo] = key.split("-");
                                        const hoursText =
                                            openingHoursFrom === "0" && openingHoursTo === "0"
                                                ? t("Closed")
                                                : openingHoursFrom + ":00 " + t("h") + " - " + openingHoursTo + ":00 " + t("h");

                                        return (
                                            <tr key={key} className={"lg:flex lg:flex-col lg:mb-1 lg:last-of-type:mb-0"}>
                                                <td className="pr-1.5 pb-1.5 font-semibold">{days}</td>
                                                <td className="pr-1.5 pb-1.5">{hoursText}</td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>

                    <div className={"col-span-12 sm:col-span-6 lg:col-span-3 flex flex-col"}>
                        <h2 className={"font-bold mb-4 text-xl text-center sm:text-left"}>{t("Follow us")}</h2>
                        <div className={"inline-flex items-center gap-x-3 justify-center sm:justify-start"}>
                            <a className={"group"} href={"https://www.facebook.com/people/ArboPark/61556596647925/"} rel="noreferrer" target={"_blank"}>
                                <FacebookFIcon className={"w-5 h-5 flex-none text-white group-hover:!text-arbo-orange"} />
                            </a>
                            <a className={"group"} href={"https://www.instagram.com/arbopark.ch/"} rel="noreferrer" target={"_blank"}>
                                <InstagramIcon className={"w-6 h-6 flex-none text-white group-hover:text-arbo-orange"} />
                            </a>
                            <a className={"group"} href={"https://www.tiktok.com/@arbopark.ch"} rel="noreferrer" target={"_blank"}>
                                <TikTokIcon className={"w-5 h-5 flex-none text-white group-hover:text-arbo-orange"} />
                            </a>
                            <a className={"group"} href={"https://www.youtube.com/@arboparkch"} rel="noreferrer" target={"_blank"}>
                                <YoutubeIcon className={"w-6 h-6 flex-none text-white group-hover:text-arbo-orange"} />
                            </a>
                            <a className={"group"} href={"https://www.linkedin.com/company/arbopark/"} rel="noreferrer" target={"_blank"}>
                                <LinkedInInIcon className={"w-6 h-6 flex-none text-white group-hover:text-arbo-orange"} />
                            </a>
                        </div>
                    </div>

                    <div className={"col-span-12 sm:col-span-6 lg:col-span-3 flex flex-col"}>
                        <h2 className={"font-bold mb-4 text-xl text-center sm:text-left"}>{t("Information")}</h2>
                        <div className={"flex flex-col gap-y-1.5"}>
                            <span className={"inline-flex items-center justify-center sm:justify-start"}>
                                <LinkIcon className={"w-3 h-3 mr-1 lg:mr-2"} />
                                <a className={"hover:text-arbo-orange"} href={LinkTo.terms()}>
                                    {t("Terms and conditions")}
                                </a>
                            </span>
                            <span className={"inline-flex items-center justify-center sm:justify-start"}>
                                <LinkIcon className={"w-3 h-3 mr-1 lg:mr-2"} />
                                <a className={"hover:text-arbo-orange"} href={LinkTo.impressum()}>
                                    {t("Impressum")}
                                </a>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookingFooterNew;
